import React from 'react';
import { Dialog, Button, Text, Divider } from '@applyboard/crystal-ui';
import { useErrorHandler } from '~/hooks/useErrorHandler';
import { useAppProcessing } from '~/pages/ApplicationDetails/context/';
import { AdditionalDocumentsRequestDetails } from '../AdditionalDocumentRequest/AdditionalDocumentsRequestDetails';
import { useDismissDocumentRequest } from '~/pages/ApplicationDetails/data/useDismissDocumentRequest';

/**
 * Dialog for the standalone "Dismiss Document Request" action.
 * Dismisses the active document request without proceeding to another status.
 */
export function DismissDocumentRequestOnlyDialog() {
  const [isBusy, setIsBusy] = React.useState(false);
  const { apiError } = useErrorHandler();
  const { completeProcess, application } = useAppProcessing();
  const { dismissDocumentRequest } = useDismissDocumentRequest(application.id);

  const onDismiss = React.useCallback(async () => {
    setIsBusy(true);
    try {
      await dismissDocumentRequest();
      completeProcess();
    } catch {
      apiError();
    } finally {
      setIsBusy(false);
    }
  }, [apiError, dismissDocumentRequest, completeProcess]);

  const additionalDocumentRequest = application.meta.additionalDocumentRequest;
  if (!additionalDocumentRequest) {
    return null;
  }

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        if (!open && !isBusy) completeProcess();
      }}
    >
      <Dialog.Heading>Dismiss Document Request</Dialog.Heading>
      <Dialog.Content>
        <Text>
          Are you sure you want to dismiss this document request? Pending documents will be marked as dismissed and the
          request will be closed. The applicant will not be expected to submit the pending documents.
        </Text>
        <Divider />
        <AdditionalDocumentsRequestDetails additionalDocumentRequest={additionalDocumentRequest} onlyPendings />
      </Dialog.Content>
      <Dialog.Actions>
        <Dialog.CloseButton width="fill">Cancel</Dialog.CloseButton>
        <Button intent="primary" width="fill" onClick={onDismiss} loading={isBusy}>
          Dismiss
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}
